import React, { useState } from 'react';
import { useAppointmentDetails, useStepNavigation } from '../organisms/ReviewWrapper';
import reviewService from '../../services/reviewService';
import apruvRateService from '../../services/apruvRateService';
import PoweredByApruv from '../common/PoweredByApruv';
import heartCircle from '../../assets/images/heart-circle.svg';

const ThankYou: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const appointmentDetails = useAppointmentDetails(); // Get from context
  const { goToTipping } = useStepNavigation();
  const rateConfig = apruvRateService.getRateConfig();

  const handleReview = async (site: 'google' | 'yelp' | 'facebook' | 'bbb') => {
    if (!appointmentDetails) return;
    setLoading(true);
    try {
      await reviewService.clickReview(appointmentDetails.appointment_details.AppointmentId);
      if (site === 'google' && appointmentDetails.appointment_details.GoogleLink) {
        window.location.href = appointmentDetails.appointment_details.GoogleLink;
      } else if (site === 'yelp' && appointmentDetails.appointment_details.YelpLink) {
        window.location.href = appointmentDetails.appointment_details.YelpLink;
      } else if (site === 'facebook' && appointmentDetails.appointment_details.FacebookLink) {
        window.location.href = appointmentDetails.appointment_details.FacebookLink;
      } else if (site === 'bbb' && appointmentDetails.appointment_details.BBBLink) {
        window.location.href = appointmentDetails.appointment_details.BBBLink;
      }
    } catch (e) {
      // Optionally handle error
    } finally {
      setLoading(false);
    }
  };

  const handleTipTech = () => {
    if (!appointmentDetails) return;
    if (reviewService.getOldReview()) {
      reviewService.setReviewStep('tipping');
    } else {
      goToTipping();
    }
  };

  if (!appointmentDetails) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ background: 'var(--color-gray-blue-900)' }}>
        <div className="review-done-loading">Loading...</div>
        <PoweredByApruv sx={{ position: 'absolute', bottom: 16, left: 0, right: 0 }} />
      </div>
    );
  }

  const { GoogleLink, YelpLink, FacebookLink, BBBLink, IsInitial, TipSent } = appointmentDetails.appointment_details;
  const { connected_account } = appointmentDetails.technician_details;

  // Process dynamic fields in thank you text
  const processDynamicFields = (text: string): string => {
    if (!text || !appointmentDetails) return text || '';

    let processedText = text;

    // Replace technician name placeholders
    const technicianName = appointmentDetails.appointment_details?.TechnicianFirstName || 
                          'Technician';
    processedText = processedText.replace(/\{\{TechnicianName\}\}/g, technicianName);
    processedText = processedText.replace(/\{\{TechnicianFirstName\}\}/g, technicianName);

    // Replace company name placeholders
    const companyName = appointmentDetails.appointment_details?.CompanyName || 
                       'Company';
    processedText = processedText.replace(/\{\{CompanyName\}\}/g, companyName);

    // Replace service date placeholders (using current date as fallback since no service date field exists)
    const serviceDate = new Date().toLocaleDateString();
    processedText = processedText.replace(/\{\{ServiceDate\}\}/g, serviceDate);

    // Replace customer name placeholders
    const customerName = appointmentDetails.appointment_details?.CustomerFirstName || 
                        'Customer';
    processedText = processedText.replace(/\{\{CustomerName\}\}/g, customerName);
    processedText = processedText.replace(/\{\{CustomerFirstName\}\}/g, customerName);

    return processedText;
  };

  const processedThankYouHeader = processDynamicFields(rateConfig?.ThankYouHeader || '');
  const processedThankYouSubHeader = processDynamicFields(rateConfig?.ThankYouSubHeader || '');

  return (
    <div className="flex flex-column h-full justify-content-center" style={{ background: 'var(--color-gray-blue-900)', minHeight: '100vh' }}>
      <div
        className="flex flex-column"
        style={{
          background: 'var(--color-gray-blue-500)',
          borderRadius: '12px',
          maxWidth: 400,
          margin: '0 auto',
          padding: '30px 18px 50px',
          boxSizing: 'border-box',
          boxShadow: '0px 10px 40px rgba(41, 50, 65, 0.06)',
          border: '1px solid var(--color-gray-blue-900)',
          alignItems: 'center',
        }}
      >
        <div className="mb-5 mt-3 flex justify-content-center">
          <img src={heartCircle} alt="Thank you" style={{ width: 48, height: 48 }} />
        </div>
        <div style={{ width: '100%' }}>
          <h2
            className="text-center"
            style={{
              fontFamily: 'var(--typography-font-family-primary)',
              fontWeight: 600,
              fontSize: 24,
              color: 'var(--color-base-white)',
              margin: 0,
              marginBottom: 0,
            }}
          >
            {processedThankYouHeader}
          </h2>
          <p
            className="text-center"
            style={{
              fontFamily: 'var(--typography-font-family-primary)',
              fontWeight: 500,
              fontSize: 16,
              color: 'var(--color-gray-400)',
              margin: 0,
              marginBottom: 0,
              marginTop: 8,
            }}
          >
            {processedThankYouSubHeader}
          </p>
        </div>
        <div style={{ width: '100%', marginTop: 32, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {GoogleLink && (
            <button
              onClick={() => handleReview('google')}
              className="tip-button"
              style={{ marginBottom: 0, color: 'var(--color-base-white) !important' }}
              disabled={loading}
            >
              Review on Google
              <i className="pi pi-fw pi-external-link" style={{ marginLeft: 8, color: 'var(--color-base-white)' }}></i>
            </button>
          )}
          {YelpLink && (
            <button
              onClick={() => handleReview('yelp')}
              className="tip-button"
              style={{ marginBottom: 0, color: 'var(--color-base-white) !important' }}
              disabled={loading}
            >
              Review on Yelp
              <i className="pi pi-fw pi-external-link" style={{ marginLeft: 8, color: 'var(--color-base-white)' }}></i>
            </button>
          )}
          {FacebookLink && (
            <button
              onClick={() => handleReview('facebook')}
              className="tip-button"
              style={{ marginBottom: 0, color: 'var(--color-base-white) !important' }}
              disabled={loading}
            >
              Review on Facebook
              <i className="pi pi-fw pi-external-link" style={{ marginLeft: 8, color: 'var(--color-base-white)' }}></i>
            </button>
          )}
          {BBBLink && (
            <>
              {GoogleLink && (
                <p
                  className="text-center"
                  style={{
                    fontFamily: 'var(--typography-font-family-primary)',
                    fontWeight: 400,
                    fontSize: 14,
                    color: 'var(--color-gray-400)',
                    margin: '8px 0',
                  }}
                >
                  If you've already left your feedback on Google, please consider leaving a review on our Better Business Bureau link.
                </p>
              )}
              <button
                onClick={() => handleReview('bbb')}
                className="tip-button"
                style={{ marginBottom: 0, color: 'var(--color-base-white) !important' }}
                disabled={loading}
              >
                Review on BBB
                <i className="pi pi-fw pi-external-link" style={{ marginLeft: 8, color: 'var(--color-base-white)' }}></i>
              </button>
            </>
          )}
          {!!IsInitial && !TipSent && !!connected_account && (
            <button
              onClick={handleTipTech}
              className="secondary-button"
              style={{ marginTop: 16 }}
            >
              Tip your Tech
            </button>
          )}
        </div>

        <PoweredByApruv sx={{ mt: 2 }} />
      </div>
    </div>
  );
};

export default ThankYou; 
